/**
 * TemplateInsertMenu pure helpers — sl-e0il.3.1
 *
 * Split out from `template-insert-menu.tsx` so unit tests can import
 * them without pulling `@affine/component` (and thus lit-react and the
 * `BUILD_CONFIG` runtime) into a Node test environment.
 */

import type {
  Insertable,
  InsertableCredential,
  InsertableInputField,
  InsertableStepField,
} from './insertable';

export function partition(insertables: readonly Insertable[]): {
  variables: Array<InsertableStepField | InsertableInputField>;
  credentials: InsertableCredential[];
} {
  const variables: Array<InsertableStepField | InsertableInputField> = [];
  const credentials: InsertableCredential[] = [];
  for (const ins of insertables) {
    if (ins.kind === 'step-field' || ins.kind === 'input-field') {
      variables.push(ins);
    } else if (ins.kind === 'credential') credentials.push(ins);
  }
  return { variables, credentials };
}

export function filterVariables(
  variables: readonly (InsertableStepField | InsertableInputField)[],
  query: string
): Array<InsertableStepField | InsertableInputField> {
  const q = query.trim().toLowerCase();
  if (!q) return variables.slice();
  return variables.filter(v => {
    if (v.kind === 'input-field') {
      return v.field.name.toLowerCase().includes(q);
    }
    return (
      v.stepLabel.toLowerCase().includes(q) ||
      v.field.name.toLowerCase().includes(q)
    );
  });
}

export function filterCredentials(
  credentials: readonly InsertableCredential[],
  query: string
): InsertableCredential[] {
  const q = query.trim().toLowerCase();
  if (!q) return credentials.slice();
  return credentials.filter(
    c =>
      c.credentialLabel.toLowerCase().includes(q) ||
      c.credentialService.toLowerCase().includes(q) ||
      c.fieldName.toLowerCase().includes(q)
  );
}

export interface GroupedVariables {
  stepId: string;
  stepLabel: string;
  items: Array<InsertableStepField | InsertableInputField>;
}

export function groupByStep(
  variables: readonly (InsertableStepField | InsertableInputField)[]
): GroupedVariables[] {
  const byId = new Map<string, GroupedVariables>();
  for (const v of variables) {
    const stepId = v.kind === 'input-field' ? '__input__' : v.stepId;
    const stepLabel = v.kind === 'input-field' ? 'Input' : v.stepLabel;
    let entry = byId.get(stepId);
    if (!entry) {
      entry = { stepId, stepLabel, items: [] };
      byId.set(stepId, entry);
    }
    entry.items.push(v);
  }
  return Array.from(byId.values());
}
